import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX } from 'react-icons/fi';

interface AddBudgetCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  catName: string;
  setCatName: (val: string) => void;
  catAllocated: string;
  setCatAllocated: (val: string) => void;
  catNotes: string;
  setCatNotes: (val: string) => void;
}

export const AddBudgetCategoryModal: React.FC<AddBudgetCategoryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  catName,
  setCatName,
  catAllocated,
  setCatAllocated,
  catNotes,
  setCatNotes,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-[#0B0B0B] border border-white/10 rounded-[28px] p-6 text-left flex flex-col max-h-[85vh] z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold text-white">Add Budget</h2>
                <p className="text-zinc-550 text-xs mt-1">
                  Set a budget allocation for a project area.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-zinc-400 text-xs font-semibold mb-2">
                  Category Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Venue, Marketing, Production"
                  value={catName}
                  onChange={(e) => setCatName(e.target.value)}
                  required
                  className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-xs font-semibold mb-2">
                  Allocated Amount
                </label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={catAllocated}
                  onChange={(e) => setCatAllocated(e.target.value)}
                  required
                  className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-xs font-semibold mb-2">
                  Notes <span className="text-zinc-550">(Optional)</span>
                </label>
                <textarea
                  placeholder="Add notes or breakdown..."
                  value={catNotes}
                  onChange={(e) => setCatNotes(e.target.value)}
                  rows={4}
                  className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 resize-none transition-all"
                />
              </div>

              <div className="flex items-center justify-end pt-4 border-t border-white/5">
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-white text-black hover:bg-zinc-100 rounded-full text-xs font-bold cursor-pointer transition-all active:scale-95 shadow-lg shadow-white/5"
                >
                  Add Category
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
